import React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faFileCsv,
} from "@fortawesome/free-solid-svg-icons";
import { CSVLink } from "react-csv";
import "./CourseAttendance.css";
import "../../App.css";
import { apiRequest } from "../../functions/requestMulti";
import { MainContext } from "../../contexts/MainContext";
import BackElement from "../../components/BackElement";
import InfoContainer from "../../components/InfoContainer";
import ErrorMessage from "../../components/ErrorMessage";
import SecButton from "../../components/SecButton";
import CourseParticipantsType from "../../components/CourseParticipantsType";
import Table from "../../components/Table/Table";
import { RowData } from "../../Types";
import { addDaysIso } from "../../functions/timetable";
import {
  AttendanceSession,
  AttendanceSessions,
  AttendanceSummaryRow,
  attendanceRate,
  sessionDateLabel,
  sessionKey,
  sessionStatusLabel,
  summaryCsvRows,
} from "../../functions/attendance";

type Props = {};
type ComposedProps = Props &
  RouteComponentProps<{
    clubId: string;
    branchId: string;
    courseId: string;
  }>;

// How much further back "Frühere Termine laden" reaches per click.
const EARLIER_DAYS = 90;

export default class CourseAttendance extends React.Component<
  ComposedProps,
  {
    clubId: string;
    branchId: string;
    courseId: string;
    showType: number; // 1 = Termine, 2 = Auswertung
    sessionsLoaded: boolean;
    sessionsData: AttendanceSessions | null;
    loadingEarlier: boolean;
    summaryLoaded: boolean;
    summaryRequested: boolean;
    summaryRows: Array<AttendanceSummaryRow>;
    listCount: number;
    errorMsg: string | null;
  }
> {
  static contextType = MainContext;
  mounted = true;

  constructor(props: ComposedProps) {
    super(props);
    this.state = {
      clubId: this.props.match.params.clubId,
      branchId: this.props.match.params.branchId,
      courseId: this.props.match.params.courseId,
      showType: 1,
      sessionsLoaded: false,
      sessionsData: null,
      loadingEarlier: false,
      summaryLoaded: false,
      summaryRequested: false,
      summaryRows: [],
      listCount: 0,
      errorMsg: null,
    };
  }

  componentDidMount = async () => {
    await this.requestSessions();
  };

  componentWillUnmount = () => {
    this.mounted = false;
  };

  requestSessions = async (from?: string) => {
    await apiRequest("api/attendance/sessions", "GET", {
      clubId: this.state.clubId,
      branchId: this.state.branchId,
      courseId: this.state.courseId,
      from,
    })
      .then(this.handleSessions)
      .catch(this.handleSessionsError);
  };

  handleSessions = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ sessionsLoaded: true, loadingEarlier: false });
    if (!data.success) {
      this.setState({ errorMsg: data.errorMsgDe });
      return;
    }
    this.setState({ sessionsData: data.data, errorMsg: null });
  };

  handleSessionsError = (err: any) => {
    console.error(err);
    if (!this.mounted) return;
    // apiRequest discards the body of a non-2xx response, so there is no
    // errorMsgDe available here.
    this.setState({
      sessionsLoaded: true,
      loadingEarlier: false,
      errorMsg: "Die Termine konnten nicht geladen werden.",
    });
  };

  loadEarlier = () => {
    const from = this.state.sessionsData?.from;
    if (!from || this.state.loadingEarlier) return;
    this.setState({ loadingEarlier: true });
    this.requestSessions(addDaysIso(from, -EARLIER_DAYS));
  };

  requestSummary = async () => {
    this.setState({ summaryRequested: true });
    await apiRequest("api/attendance/summary", "GET", {
      clubId: this.state.clubId,
      branchId: this.state.branchId,
      courseId: this.state.courseId,
    })
      .then(this.handleSummary)
      .catch(this.handleSummaryError);
  };

  handleSummary = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ summaryLoaded: true });
    if (!data.success) {
      this.setState({ errorMsg: data.errorMsgDe });
      return;
    }
    this.setState({
      summaryRows: data.data?.rows || [],
      listCount: data.data?.listCount || 0,
      errorMsg: null,
    });
  };

  handleSummaryError = (err: any) => {
    console.error(err);
    if (!this.mounted) return;
    this.setState({
      summaryLoaded: true,
      errorMsg: "Die Auswertung konnte nicht geladen werden.",
    });
  };

  selectShowType = (showType: number) => {
    this.setState({ showType });
    if (showType === 2 && !this.state.summaryRequested) {
      this.requestSummary();
    }
  };

  listPath = (session: { date: string; startTime: string }) =>
    `/club/${this.state.clubId}/branch/${this.state.branchId}/attendance/${
      this.state.courseId
    }/${sessionKey(session.date, session.startTime)}`;

  handleRedirect = (redirect: string | null) => {
    // Table calls this even for rows with onClick: null.
    if (!redirect) return;
    this.props.history.push({ pathname: redirect });
  };

  sessionRows = (): Array<RowData> => {
    const today = this.state.sessionsData?.today || "";
    return (this.state.sessionsData?.sessions || []).map(
      (session: AttendanceSession) => {
        const dateLabel = sessionDateLabel(session.date);
        return {
          cells: [
            {
              value: (
                <>
                  {session.date === today && (
                    <span className="attendance-today-tag">Heute</span>
                  )}
                  {dateLabel}
                  {session.sessionTitle && (
                    <div className="attendance-session-title">
                      {session.sessionTitle}
                    </div>
                  )}
                  {!session.scheduled && (
                    <div className="attendance-session-title">
                      Termin nicht mehr im Kursplan
                    </div>
                  )}
                </>
              ),
              sort: `${session.date} ${session.startTime}`,
              search: dateLabel,
            },
            {
              value: `${session.startTime} - ${session.endTime} Uhr`,
              sort: session.startTime,
            },
            {
              value: sessionStatusLabel(session),
              sort: session.listId === null ? -1 : session.presentCount,
            },
          ],
          onClick: this.listPath(session),
          // Upcoming sessions can be prepared, but are not the usual target.
          opacity: session.date > today ? 0.6 : undefined,
        };
      }
    );
  };

  summaryRowsTable = (): Array<RowData> =>
    this.state.summaryRows.map((row) => {
      const rate = attendanceRate(row.attended, row.total);
      return {
        cells: [
          { value: row.fName, sort: row.fName, search: row.fName },
          { value: row.lName, sort: row.lName, search: row.lName },
          { value: row.attended, sort: row.attended },
          { value: row.total, sort: row.total },
          { value: `${rate} %`, sort: rate },
        ],
        onClick: null,
      };
    });

  render() {
    const data = this.state.sessionsData;
    const course = data?.course;
    const todaySessions = (data?.sessions || []).filter(
      (session) => session.date === data?.today && session.scheduled
    );

    return (
      <div className="subscreen-main-container">
        <div className="subscreen-box-container">
          <BackElement
            text="zurück zu den Teilnehmern"
            to={`/club/${this.state.clubId}/branch/${this.state.branchId}/participants/${this.state.courseId}`}
          />
          <div className="course-title-print-container">
            <h1>Anwesenheit</h1>
            {this.state.showType === 2 && this.state.summaryLoaded && (
              <CSVLink
                data={summaryCsvRows(this.state.summaryRows)}
                filename={`Anwesenheit_${course?.title || "Kurs"}_${
                  data?.today || ""
                }.csv`}
              >
                <FontAwesomeIcon
                  icon={faFileCsv}
                  size="lg"
                  className="course-print-icon"
                />
              </CSVLink>
            )}
          </div>
          {course && <h2 className="attendance-course-title">{course.title}</h2>}
          <ErrorMessage message={this.state.errorMsg} />
          <CourseParticipantsType
            type={this.state.showType}
            onChange={this.selectShowType}
            firstName="Termine"
            secondName="Auswertung"
          />
          {this.state.showType === 1 ? (
            <>
              {todaySessions.map((session) => (
                <div className="inline-block" key={session.startTime}>
                  <Link className="create-btn" to={this.listPath(session)}>
                    <div className="create-icon-container">
                      <FontAwesomeIcon icon={faClipboardCheck} size="1x" />
                    </div>
                    Liste für heute, {session.startTime} Uhr öffnen
                  </Link>
                </div>
              ))}
              <Table
                header={[
                  { title: "Termin" },
                  { title: "Uhrzeit", displayWidth: 600 },
                  { title: "Anwesenheit" },
                ]}
                data={this.sessionRows()}
                loaded={this.state.sessionsLoaded}
                skeletonRows={5}
                onClick={this.handleRedirect}
                defaultSortIndex={0}
                defaultSortDirection="desc"
              />
              {this.state.sessionsLoaded && data?.sessions.length === 0 && (
                <div className="full-center">
                  <span className="not-exist-text">
                    In diesem Zeitraum hat der Kurs keine Termine.
                  </span>
                </div>
              )}
              {data?.hasEarlier && (
                <div className="attendance-load-earlier">
                  <SecButton
                    change
                    loading={this.state.loadingEarlier}
                    onClick={this.loadEarlier}
                    title="Frühere Termine laden"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <InfoContainer>
                Gezählt werden nur Termine, für die eine Anwesenheitsliste
                geöffnet wurde
                {this.state.summaryLoaded
                  ? ` (bisher ${this.state.listCount})`
                  : ""}
                . &quot;Termine&quot; sind die Listen, auf denen die Person
                stand.
              </InfoContainer>
              <Table
                header={[
                  { title: "Vorname" },
                  { title: "Nachname" },
                  { title: "Anwesend" },
                  { title: "Termine" },
                  { title: "Quote" },
                ]}
                data={this.summaryRowsTable()}
                loaded={this.state.summaryLoaded}
                skeletonRows={5}
                onClick={this.handleRedirect}
                defaultSortIndex={1}
                defaultSortDirection="asc"
                counter
              />
              {this.state.summaryLoaded &&
                this.state.summaryRows.length === 0 && (
                  <div className="full-center">
                    <span className="not-exist-text">
                      Für diesen Kurs wurde noch keine Anwesenheit erfasst.
                    </span>
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    );
  }
}
